import {
  Dropdown,
  Field,
  Input,
  Option,
  makeStyles,
} from '@fluentui/react-components';
import { FIELD_LABEL_LAYOUT } from '@moko/ui';
import type {
  IBillingClass,
  IPricingTier,
} from '../../services/settingsService';

export type TenantPricingValue = {
  pricingTierId: number | null;
  pricingDiscountPercentage: number | null;
  billingClassId: number | null;
  defaultTimeServiceId: number | null;
};

export type TenantTimeServiceOption = {
  id: number;
  name: string;
  code: string;
  isActive: boolean;
};

export interface TenantPricingFieldsProps {
  value: TenantPricingValue;
  tiers: IPricingTier[];
  timeServices: TenantTimeServiceOption[];
  billingClasses: IBillingClass[];
  onChange: (value: TenantPricingValue) => void;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
    width: '100%',
    maxWidth: '520px',
  },
  control: {
    minWidth: 'auto',
    width: '100%',
  },
});

const TenantPricingFields = ({
  value,
  tiers,
  timeServices,
  billingClasses,
  onChange,
}: TenantPricingFieldsProps) => {
  const styles = useStyles();
  const selected = tiers.find((tier) => tier.id === value.pricingTierId);
  const tierLabel = selected?.name || selected?.code || '';
  const timeServiceChoices = timeServices.filter(
    (service) => service.isActive || service.id === value.defaultTimeServiceId
  );
  const selectedTimeService = timeServices.find(
    (service) => service.id === value.defaultTimeServiceId
  );
  const timeServiceLabel =
    selectedTimeService?.name || selectedTimeService?.code || 'None';
  const classChoices = billingClasses.filter(
    (billingClass) => typeof billingClass.id === 'number'
  );
  const selectedClass = billingClasses.find(
    (billingClass) => billingClass.id === value.billingClassId
  );
  const billingClassLabel =
    selectedClass?.name ||
    selectedClass?.code ||
    (value.billingClassId == null ? 'None' : String(value.billingClassId));

  return (
    <div className={styles.root}>
      <Field
        label="Pricing tier"
        orientation="horizontal"
        style={FIELD_LABEL_LAYOUT}
      >
        <Dropdown
          className={styles.control}
          value={tierLabel}
          selectedOptions={
            value.pricingTierId == null ? [] : [String(value.pricingTierId)]
          }
          onOptionSelect={(_, data) => {
            const next = data.optionValue ? Number(data.optionValue) : null;
            onChange({
              ...value,
              pricingTierId: Number.isFinite(next) ? next : null,
            });
          }}
        >
          {tiers
            .filter((tier) => tier.id != null)
            .map((tier) => (
              <Option key={tier.id} value={String(tier.id)}>
                {tier.name || tier.code}
              </Option>
            ))}
        </Dropdown>
      </Field>
      <Field
        label="Discount %"
        orientation="horizontal"
        style={FIELD_LABEL_LAYOUT}
      >
        <Input
          className={styles.control}
          type="number"
          value={
            value.pricingDiscountPercentage == null
              ? ''
              : String(value.pricingDiscountPercentage)
          }
          onChange={(_, data) => {
            if (data.value === '') {
              onChange({ ...value, pricingDiscountPercentage: null });
              return;
            }
            const parsed = Number(data.value);
            onChange({
              ...value,
              pricingDiscountPercentage: Number.isFinite(parsed)
                ? parsed
                : null,
            });
          }}
        />
      </Field>
      <Field
        label="Default time service"
        orientation="horizontal"
        style={FIELD_LABEL_LAYOUT}
      >
        <Dropdown
          className={styles.control}
          value={timeServiceLabel}
          selectedOptions={
            value.defaultTimeServiceId == null
              ? ['none']
              : [String(value.defaultTimeServiceId)]
          }
          onOptionSelect={(_, data) => {
            const next = Number(data.optionValue);
            onChange({
              ...value,
              defaultTimeServiceId:
                data.optionValue === 'none' || !Number.isInteger(next)
                  ? null
                  : next,
            });
          }}
        >
          <Option value="none">None</Option>
          {timeServiceChoices.map((service) => (
            <Option key={service.id} value={String(service.id)}>
              {service.name || service.code}
            </Option>
          ))}
        </Dropdown>
      </Field>
      <Field
        label="Billing class"
        orientation="horizontal"
        style={FIELD_LABEL_LAYOUT}
      >
        <Dropdown
          className={styles.control}
          value={billingClassLabel}
          selectedOptions={
            value.billingClassId == null
              ? ['none']
              : [String(value.billingClassId)]
          }
          onOptionSelect={(_, data) => {
            const next = Number(data.optionValue);
            onChange({
              ...value,
              billingClassId:
                data.optionValue === 'none' || !Number.isInteger(next)
                  ? null
                  : next,
            });
          }}
        >
          <Option value="none">None</Option>
          {classChoices.map((billingClass) => (
            <Option key={billingClass.id} value={String(billingClass.id)}>
              {billingClass.name || billingClass.code}
            </Option>
          ))}
          {value.billingClassId != null &&
            !classChoices.some(
              (billingClass) => billingClass.id === value.billingClassId
            ) && (
              <Option value={String(value.billingClassId)}>
                {String(value.billingClassId)}
              </Option>
            )}
        </Dropdown>
      </Field>
    </div>
  );
};

export default TenantPricingFields;
